import Box from "@mui/material/Box";
import {forwardRef, useImperativeHandle, useRef} from "react";
import {useTranslation} from "react-i18next";
import VariablePicker, {type VariablePickerHandle} from "../../common/variables/VariablePicker";
import {
    printoutPickerVariablesByReference,
    toPrintoutPickerVariables,
    type PrintoutPickerVariable,
} from "./printoutPickerVariables";

export interface PrintoutVariableMenuHandle {
    onKeyDown: (event: KeyboardEvent) => boolean;
}

interface PrintoutVariableMenuProps {
    items: PrintoutPickerVariable[];
    query?: string;
    command: (item: PrintoutPickerVariable) => void;
    clientRect?: (() => DOMRect | null) | null;
}

const MENU_WIDTH = 380;
const MENU_MAX_HEIGHT = 460;

// Portalled to body so canvas zoom does not scale it; keyboard focus stays in the editor.
const PrintoutVariableMenu = forwardRef<PrintoutVariableMenuHandle, PrintoutVariableMenuProps>(
    ({items, query, command, clientRect}, ref) => {
        const {t} = useTranslation();
        const picker = useRef<VariablePickerHandle>(null);

        useImperativeHandle(ref, () => ({
            onKeyDown: (event: KeyboardEvent) => picker.current?.onKeyDown(event) ?? false,
        }));

        const rect = clientRect?.();
        const byReference = printoutPickerVariablesByReference(items);

        return (
            <Box
                sx={theme => ({
                    position: "fixed",
                    top: Math.min((rect?.bottom ?? 0) + 6, window.innerHeight - MENU_MAX_HEIGHT),
                    left: Math.min(rect?.left ?? 0, window.innerWidth - MENU_WIDTH - 16),
                    // Keep unsaved-changes dialogs above the picker.
                    zIndex: theme.zIndex.modal - 1,
                })}
            >
                <VariablePicker
                    ref={picker}
                    width={MENU_WIDTH}
                    variables={toPrintoutPickerVariables(items, t)}
                    query={query ?? ""}
                    onSelect={picked => {
                        const item = byReference.get(picked.key);
                        if (item) command(item);
                    }}
                />
            </Box>
        );
    },
);

PrintoutVariableMenu.displayName = "PrintoutVariableMenu";

export default PrintoutVariableMenu;
